import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import * as THREE from 'three';
import { squareToVec3 } from './scene.js';
import { DEFAULT_BOARD_THEME, type BoardTheme } from './themes.js';

const boardGeometry = new THREE.BoxGeometry(1, 0.15, 1);

/**
 * Build a 2D square shape with rounded corners, used to extrude the board's
 * outer frame so it matches the rounded `rounded-3xl` container it sits in.
 *
 * `size` is the side length; `radius` is the corner radius (clamped to half the
 * size so the shape stays non-degenerate).
 */
function createRoundedSquareShape(size: number, radius: number): THREE.Shape {
  const shape = new THREE.Shape();
  const half = size / 2;
  const r = Math.min(radius, half);
  // Trace the outline clockwise starting just past the top-right corner.
  shape.moveTo(half - r, half);
  shape.lineTo(-half + r, half);
  shape.quadraticCurveTo(-half, half, -half, half - r);
  shape.lineTo(-half, -half + r);
  shape.quadraticCurveTo(-half, -half, -half + r, -half);
  shape.lineTo(half - r, -half);
  shape.quadraticCurveTo(half, -half, half, -half + r);
  shape.lineTo(half, half - r);
  shape.quadraticCurveTo(half, half, half - r, half);
  return shape;
}

export function Board({ theme = DEFAULT_BOARD_THEME }: { theme?: BoardTheme }) {
  const lightMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: theme.light, roughness: 0.8 }),
    [theme],
  );
  const darkMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: theme.dark, roughness: 0.8 }),
    [theme],
  );
  // Frame: a single rounded slab slightly larger than the 8x8 squares, extruded
  // downward so its top sits a hair below the squares' top. Color matches the
  // CSS `--color-board-frame` token; Phase 3 will theme this properly.
  // Geometry is memoized once — it never changes.
  const frameGeometry = useMemo(() => {
    const shape = createRoundedSquareShape(9.5, 0.25);
    return new THREE.ExtrudeGeometry(shape, {
      depth: 0.25,
      bevelEnabled: true,
      bevelSize: 0.03,
      bevelThickness: 0.03,
      bevelSegments: 2,
    });
  }, []);
  const frameMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: '#3a3447', roughness: 0.7, metalness: 0.1 }),
    [],
  );

  const squares = useMemo(() => {
    const list: Array<{ key: string; pos: [number, number, number]; mat: THREE.MeshStandardMaterial }> =
      [];
    for (let rank = 8; rank >= 1; rank--) {
      for (let file = 0; file < 8; file++) {
        const square = `${String.fromCharCode(97 + file)}${rank}`;
        const [x, , z] = squareToVec3(square);
        const dark = (file + rank) % 2 === 1;
        list.push({ key: square, pos: [x, -0.075, z], mat: dark ? darkMat : lightMat });
      }
    }
    return list;
  }, [darkMat, lightMat]);

  return (
    <group>
      {/* Frame sits below the squares: extrusion axis becomes world +Y after
          the rotation. The bevel adds bevelThickness (0.03) to BOTH faces, so
          the slab spans [-0.03, 0.28] along the extrusion axis; a position of
          -0.30 puts the frame's top at y=-0.02 (just below the squares' top at
          y=0). At the old -0.27 the top sat at y=+0.01 and the frame covered
          every square — the board rendered as a flat dark slab.
          9.5 wide so it shows a 0.75-unit rim around the 8x8 squares. */}
      <mesh
        geometry={frameGeometry}
        material={frameMat}
        position={[0, -0.3, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      />
      {squares.map((s) => (
        <mesh key={s.key} geometry={boardGeometry} position={s.pos} material={s.mat} />
      ))}
    </group>
  );
}
